import type React from 'react';
import { useState } from 'react';
import { Table } from 'antd';
import { useStrapiQuery } from 'hooks/useStrapiQuery';
import type { SorterResult } from 'antd/lib/table/interface';
import { notifyError } from 'utils/notifications';
import { useResellersTrialCreditsFilterWithPagination } from '../useFilterWithPagination';
import {
  GET_RESELLERS_DATA,
  type ResellersDataItem,
  type ResellersData,
  type ResellersVariables,
} from '../queries/getResellersData';
import { getColumns } from '../utils/getColumns';
import { ResellerTrialCreditsModal } from './Modal';

export const ResellerTrialCreditsTable: React.VFC = () => {
  const [editedResellerId, setEditedResellerId] = useState<ResellersDataItem['id']>(undefined);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const { filter, pagination, setPaginationState, setFilterState } = useResellersTrialCreditsFilterWithPagination();

  const {
    data: { resellers: { data: resellers = [] } = {} } = {},
    loading: isLoadingResellers,
    refetch: refetchResellers,
  } = useStrapiQuery<ResellersData, ResellersVariables>(GET_RESELLERS_DATA, {
    variables: {
      sort: [filter.sort],
      limit: pagination.pageSize,
      start: (pagination.current - 1) * pagination.pageSize,
    },
    onError(error) {
      notifyError(error, {
        message: `Error while fetching resellers. Please try again.`,
      });
    },
    onCompleted({ resellers: { meta } = {} }) {
      setPaginationState({
        total: meta?.pagination.total ?? 0,
      });
    },
    fetchPolicy: 'cache-and-network',
  });

  const handleTableChange = (sorter: SorterResult<(typeof resellers)[number]>) => {
    const sorterField = Array.isArray(sorter.field) ? sorter.field.slice(-1) : sorter.field;
    setFilterState({
      ...(sorter.order &&
        sorterField && {
          sort: `${sorterField as string}:${sorter.order.slice(0, -3)}`,
        }),
    });
  };

  const handleEdit = (item: ResellersDataItem) => {
    setIsModalVisible(true);
    setEditedResellerId(item.id);
  };

  const handleModalHide = () => {
    setIsModalVisible(false);
    setEditedResellerId(undefined);
  };

  const handleSuccess = () => {
    handleModalHide();
    refetchResellers();
  };

  const columns = getColumns(handleEdit);

  return (
    <>
      {isModalVisible && (
        <ResellerTrialCreditsModal
          reseller={resellers.find(({ id }) => id === editedResellerId)}
          onHide={handleModalHide}
          onSuccess={handleSuccess}
        />
      )}
      <Table
        columns={columns}
        dataSource={resellers}
        loading={isLoadingResellers}
        pagination={{
          ...pagination,
          showTotal(total, range) {
            return `${range[0]}-${range[1]} of ${total} Resellers`;
          },
        }}
        rowKey={row => row.id}
        scroll={{ x: true }}
        onChange={(_, __, sorter) => handleTableChange(Array.isArray(sorter) ? sorter[0] : sorter)}
      />
    </>
  );
};
